/**
 * Changelog — trigger no header + sidebar lateral com cards expansíveis.
 */

import React, { useState, useEffect } from 'react';
import { Megaphone } from 'lucide-react';
import packageJson from '../../../package.json';
import { getApiBaseUrl } from '../../utils/apiConfig';
import {
  entryMatchesVersion,
  getChangelogSeen,
  CHANGELOG_SEEN_KEY,
} from './changelogUtils';
import { ChangelogSidebar } from './ChangelogSidebar';
import './ChangelogDropdown.css';
import '../board/toolbar/BoardClickUpFilterChip.css';

export default function ChangelogDropdown({ apiBaseUrl, isOpen, onToggle }) {
  const currentVersion = packageJson.version;
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [seenForVersion, setSeenForVersion] = useState(() => getChangelogSeen(currentVersion));

  const entriesForVersion = (entries || []).filter((e) => entryMatchesVersion(e, currentVersion));
  const hasUpdates = !loading && entriesForVersion.length > 0;
  const showBadge = hasUpdates && !isOpen && !seenForVersion;

  useEffect(() => {
    if (currentVersion && isOpen) {
      try {
        localStorage.setItem(CHANGELOG_SEEN_KEY + currentVersion.trim(), '1');
      } catch (_) {}
      setSeenForVersion(true);
    }
  }, [currentVersion, isOpen]);

  useEffect(() => {
    if (currentVersion) {
      setSeenForVersion(getChangelogSeen(currentVersion));
    }
  }, [currentVersion]);

  useEffect(() => {
    const base = apiBaseUrl || getApiBaseUrl();
    const url = `${base}/api/canny/changelog?limit=20`;
    setLoading(true);
    setError(null);
    fetch(url, { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => {
        setEntries(Array.isArray(data.entries) ? data.entries : []);
      })
      .catch((err) => {
        setError(err.message || 'Erro ao carregar');
        setEntries([]);
      })
      .finally(() => setLoading(false));
  }, [apiBaseUrl]);

  const handleClose = () => {
    if (isOpen) onToggle?.();
  };

  return (
    <>
      <ChangelogTriggerButton onClick={onToggle} isOpen={isOpen} hasUpdates={showBadge} />
      <ChangelogSidebar
        isOpen={isOpen}
        onClose={handleClose}
        entries={entriesForVersion}
        loading={loading}
        error={error}
        version={currentVersion}
      />
    </>
  );
}

export function ChangelogTriggerButton({ onClick, isOpen, title, hasUpdates }) {
  const tooltipText = title || 'Últimas atualizações';

  return (
    <div className="changelog-trigger-anchor board-clickup-icon-btn-anchor">
      <button
        type="button"
        className={`changelog-trigger${isOpen ? ' is-open' : ''}`}
        onClick={onClick}
        aria-label={tooltipText}
        aria-expanded={isOpen}
      >
        <span className="changelog-trigger-icon-wrap">
          <Megaphone size={16.94} strokeWidth={2} aria-hidden="true" />
          {hasUpdates ? <span className="changelog-trigger-dot" aria-hidden="true" /> : null}
        </span>
      </button>
      {!isOpen ? (
        <span className="board-clickup-tooltip board-clickup-tooltip--bottom" role="tooltip">
          {tooltipText}
        </span>
      ) : null}
    </div>
  );
}
